import React, { useEffect, useMemo, useState } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminAPI, LLMPricing as LLMPricingRow, UpdateLLMPricingBody, Modality } from '../lib/api';
import { Cpu, Loader2, History, Check, Volume2, Mic } from 'lucide-react';

const PROVIDER_LABEL: Record<string, string> = {
  groq: 'Groq',
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  elevenlabs: 'ElevenLabs',
};

const PROVIDER_PIN: Record<string, string> = {
  groq: 'bg-orange-50 text-orange-700 border-orange-200',
  anthropic: 'bg-amber-50 text-amber-700 border-amber-200',
  openai: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  elevenlabs: 'bg-pink-50 text-pink-700 border-pink-200',
};

const MODALITY_META: Record<Modality, { title: string; blurb: string; icon: React.ReactNode }> = {
  llm: {
    title: 'Language models (LLM)',
    blurb: 'Per 1 million tokens — input and output billed separately.',
    icon: <Cpu className="w-5 h-5 text-blue-600" />,
  },
  tts: {
    title: 'Text-to-speech',
    blurb: 'Per 1 million characters synthesised (ElevenLabs bills per character).',
    icon: <Volume2 className="w-5 h-5 text-pink-600" />,
  },
  stt: {
    title: 'Speech-to-text (Whisper)',
    blurb: 'Per minute of audio transcribed.',
    icon: <Mic className="w-5 h-5 text-teal-600" />,
  },
};

/** The short name for a rate's type, where a table cell has no room for the section title. */
const MODALITY_SHORT: Record<Modality, string> = {
  llm: 'Language model',
  tts: 'Text-to-speech',
  stt: 'Speech-to-text',
};

const MODALITY_ORDER: Modality[] = ['llm', 'tts', 'stt'];
const md = (r: LLMPricingRow): Modality => (r.modality || 'llm') as Modality;

/** A USD rate with thousands separators and no trailing zeros; a missing rate is a dash, never $0. */
const money = (n: number | null | undefined): string =>
  n == null || Number.isNaN(n) ? '—' : `$${n.toLocaleString('en-US', { maximumFractionDigits: 4 })}`;

export const LLMPricingPage: React.FC = () => {
  const { token } = useAdminAuth();
  const api = useMemo(() => new AdminAPI(token!), [token]);
  const [rows, setRows] = useState<LLMPricingRow[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      setRows(await api.getLLMPricing());
    } catch (e) { setErr((e as Error).message); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const current = rows.filter(r => !r.effective_to);
  const history = rows.filter(r => r.effective_to);
  const initialLoad = loading && rows.length === 0;

  const onSave = async (id: number, body: UpdateLLMPricingBody) => {
    await api.updateLLMPricing(id, body);
    await refresh();
  };

  return (
    <div className="max-w-5xl space-y-8">
      <div className="flex items-center space-x-3">
        <Cpu className="w-7 h-7 text-blue-600" />
        <h1 className="text-2xl font-bold text-gray-900">Model pricing</h1>
      </div>

      <p className="text-sm text-gray-600">
        Rates used to compute per-call USD cost in Usage &amp; Cost, across language
        models, text-to-speech, and speech-to-text. Defaults reflect public provider
        pricing — set your real contracted rates here. Saving a rate opens a new effective
        period; past events keep their original cost.
      </p>

      {err && <Banner kind="error">{err}</Banner>}

      {loading && (
        <div className="text-sm text-gray-500">
          <Loader2 className="w-4 h-4 inline animate-spin mr-2" />{initialLoad ? 'Loading rates…' : 'Refreshing…'}
        </div>
      )}

      {!initialLoad && MODALITY_ORDER.map((m) => (
        <ModalitySection key={m} modality={m} rows={current.filter(r => md(r) === m)} onSave={onSave} />
      ))}

      {history.length > 0 && (
        <section className="bg-white shadow rounded-lg">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50"
          >
            <div className="flex items-center space-x-2">
              <History className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">
                Pricing history ({history.length} archived rate{history.length !== 1 ? 's' : ''})
              </span>
            </div>
            <span className="text-xs text-gray-500">{showHistory ? 'Hide' : 'Show'}</span>
          </button>
          {showHistory && (
            <div className="border-t border-gray-200 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                  <tr>
                    <th className="px-6 py-2 text-left">Provider</th>
                    <th className="px-6 py-2 text-left">Model</th>
                    <th className="px-6 py-2 text-left">Type</th>
                    <th className="px-6 py-2 text-right">Rate</th>
                    <th className="px-6 py-2 text-left">Effective from → to</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {history.map(r => (
                    <tr key={r.id} className="text-gray-500">
                      <td className="px-6 py-2">{PROVIDER_LABEL[r.provider] || r.provider}</td>
                      <td className="px-6 py-2 font-mono text-xs">{r.model}</td>
                      <td className="px-6 py-2 text-xs">{MODALITY_SHORT[md(r)]}</td>
                      <td className="px-6 py-2 text-right tabular-nums">{rateSummary(r)}</td>
                      <td className="px-6 py-2 text-xs">
                        {new Date(r.effective_from).toLocaleDateString()} →{' '}
                        {r.effective_to ? new Date(r.effective_to).toLocaleDateString() : 'now'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
};

// Compact one-line summary of a row's active rate, for the history table.
const rateSummary = (r: LLMPricingRow): string => {
  const m = md(r);
  if (m === 'tts') return r.per_million_chars == null ? '—' : `${money(r.per_million_chars)} per 1M characters`;
  if (m === 'stt') return r.per_audio_minute == null ? '—' : `${money(r.per_audio_minute)} per audio minute`;
  return `${money(r.input_per_mtok)} / ${money(r.output_per_mtok)} per 1M tokens`;
};

const ModalitySection: React.FC<{
  modality: Modality;
  rows: LLMPricingRow[];
  onSave: (id: number, body: UpdateLLMPricingBody) => Promise<void>;
}> = ({ modality, rows, onSave }) => {
  const meta = MODALITY_META[modality];
  const columns = modality === 'llm' ? 6 : 5;
  return (
    <section className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center space-x-3">
        {meta.icon}
        <div>
          <h2 className="text-sm font-semibold text-gray-900">{meta.title}</h2>
          <p className="text-xs text-gray-500">{meta.blurb}</p>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Provider</th>
              {modality === 'llm' && <>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Input $ per 1M tokens</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Output $ per 1M tokens</th>
              </>}
              {modality === 'tts' && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">$ per 1M characters</th>}
              {modality === 'stt' && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">$ per audio minute</th>}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Effective from</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.map(r => <PricingRow key={r.id} row={r} onSave={onSave} />)}
            {rows.length === 0 && (
              <tr>
                <td colSpan={columns} className="px-6 py-6 text-center text-sm text-gray-400">
                  No current rates for this type yet. Rows are added outside this page; once one exists it can be edited here.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const numInput = 'w-32 rounded border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500';

const PricingRow: React.FC<{
  row: LLMPricingRow;
  onSave: (id: number, body: UpdateLLMPricingBody) => Promise<void>;
}> = ({ row, onSave }) => {
  const modality = md(row);
  const [input, setInput] = useState(row.input_per_mtok);
  const [output, setOutput] = useState(row.output_per_mtok);
  const [perMchar, setPerMchar] = useState(row.per_million_chars ?? 0);
  const [perMin, setPerMin] = useState(row.per_audio_minute ?? 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setInput(row.input_per_mtok);
    setOutput(row.output_per_mtok);
    setPerMchar(row.per_million_chars ?? 0);
    setPerMin(row.per_audio_minute ?? 0);
  }, [row.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty =
    modality === 'llm' ? (input !== row.input_per_mtok || output !== row.output_per_mtok)
    : modality === 'tts' ? (perMchar !== (row.per_million_chars ?? 0))
    : (perMin !== (row.per_audio_minute ?? 0));

  const buildBody = (): UpdateLLMPricingBody =>
    modality === 'llm' ? { input_per_mtok: input, output_per_mtok: output }
    : modality === 'tts' ? { per_million_chars: perMchar }
    : { per_audio_minute: perMin };

  // Friendly equivalent shown under the editable field.
  const hint =
    modality === 'tts' ? `≈ ${money(perMchar / 1000)} per 1k characters`
    : modality === 'stt' ? `≈ ${money(perMin * 60)} per audio hour`
    : null;

  return (
    <tr>
      <td className="px-6 py-3 text-sm text-gray-700 font-mono text-xs">{row.model}</td>
      <td className="px-6 py-3">
        <span className={`text-xs font-medium px-2 py-1 rounded border ${PROVIDER_PIN[row.provider] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
          {PROVIDER_LABEL[row.provider] || row.provider}
        </span>
      </td>
      {modality === 'llm' && <>
        <td className="px-6 py-3">
          <input type="number" step="0.000001" value={input}
            onChange={(e) => setInput(Number(e.target.value))} className={numInput} />
        </td>
        <td className="px-6 py-3">
          <input type="number" step="0.000001" value={output}
            onChange={(e) => setOutput(Number(e.target.value))} className={numInput} />
        </td>
      </>}
      {modality === 'tts' && (
        <td className="px-6 py-3">
          <input type="number" step="0.000001" value={perMchar}
            onChange={(e) => setPerMchar(Number(e.target.value))} className={numInput} />
          {hint && <div className="text-[11px] text-gray-400 mt-1">{hint}</div>}
        </td>
      )}
      {modality === 'stt' && (
        <td className="px-6 py-3">
          <input type="number" step="0.000001" value={perMin}
            onChange={(e) => setPerMin(Number(e.target.value))} className={numInput} />
          {hint && <div className="text-[11px] text-gray-400 mt-1">{hint}</div>}
        </td>
      )}
      <td className="px-6 py-3 text-xs text-gray-500">{new Date(row.effective_from).toLocaleDateString()}</td>
      <td className="px-6 py-3 text-right">
        <button
          disabled={!dirty || saving}
          onClick={async () => { setSaving(true); try { await onSave(row.id, buildBody()); } finally { setSaving(false); } }}
          className={`px-3 py-1 text-xs rounded font-medium inline-flex items-center ${
            dirty ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          } disabled:opacity-50`}
        >
          <Check className="w-3 h-3 mr-1" />{saving ? 'Saving…' : 'Save'}
        </button>
      </td>
    </tr>
  );
};

const Banner: React.FC<{ kind: 'error' | 'info'; children: React.ReactNode }> = ({ kind, children }) => (
  <div className={`px-4 py-3 rounded text-sm border ${
    kind === 'error'
      ? 'bg-red-50 border-red-200 text-red-700'
      : 'bg-blue-50 border-blue-200 text-blue-700'
  }`}>
    {children}
  </div>
);
